import { ft } from '../core/units.js';
import { createRng, hashSeed } from './rng.js';
import { penetrates, resolveHorizontal, supportHeight } from '../core/collision.js';
import { EVENT } from './events.js';
import { OBJECTS, hasTag } from '../data/objects.js';

/**
 * What else is out there.
 *
 * A spawner and two behaviours, and no encounters. Nothing here is triggered by
 * where you are in anything; a roll happens every few minutes, it is weighted
 * by the hour and by the room, and whatever comes up comes up. A javelina on
 * the apron at two in the morning is not a moment somebody wrote. It is the
 * hour javelinas are about and the place they are about it.
 *
 * The small ones are spawned straight into the entity store as ordinary
 * carryable objects, which means every single thing you can do with an orange
 * you can do with a scorpion, through code that does not know either exists.
 * That is the entire implementation of "catchable therefore throwable".
 *
 * The big ones are not objects. They have a position and one of two habits —
 * keep away, or come at you — and the counterplay to being run at is the
 * counterplay to being run at: get behind something, get above something, or be
 * somewhere else.
 *
 * And they are hungry, some of them, which is the only thing in here that is a
 * want rather than a habit. Food on the ground is somewhere to go; food in
 * somebody's hands is somebody to follow. Which means a herd goes where the
 * food goes, and the food goes where you carry it — and what the building makes
 * of a herd turning up somewhere is the building's business, decided by the
 * same people who decide what it makes of you.
 *
 * Who is about is not asked here. It is asked of `sense`, which is perception
 * pointed the other way: the player and the Banquet Manager are the same kind
 * of thing to an animal, at the same distance, and there is one place that
 * says so.
 *
 * Everything stays on its own ground. An animal goes where its row says it
 * lives and not a step further, so a herd led to the dock stands on the apron
 * at the foot of the doors, which is where the desert ends and the building
 * begins.
 */

/** Game minutes between rolls. */
const EVERY = 4;

/** How many of the big ones can be about at once — as groups, so a herd is one. */
const BIG_LIMIT = 2;

/** How many small ones may be lying around before the desert stops sending more. */
const SMALL_LIMIT = 12;

/** Metres. How close a charge has to get to count as having got you. */
const CONTACT = 1.1;

/** Seconds a charge lasts before the thing loses interest. */
const CHARGE_FOR = 4;

const BODY = 1.1;

/**
 * Seconds after it has got to somebody before it will go again.
 *
 * Without it, an animal that arrived was still arriving on the next frame, and
 * the one after: standing still next to a javelina was sixty collisions a
 * second. Having got to you, it has made its point.
 */
const REST = 3;

/** Metres. How close an animal gets to food on the ground before it eats it. */
const MOUTH = 0.5;

/** Seconds of eating one piece of food takes. */
const EAT = 4;

/** Metres. How far apart two of a herd stand, rather than inside each other. */
const ELBOW = 0.9;

/** Whether an hour is inside a wrapping [from, to] window. */
export function withinHours(hours, hour) {
  const [from, to] = hours;
  if (from <= to) return hour >= from && hour < to;
  return hour >= from || hour < to;
}

export function createWildlife({ table, entities, world, bus, rooms, seed = 'lion-wild', sense = null }) {
  /** The big ones. The small ones live in the entity store like everything else. */
  let creatures = [];
  const rows = Object.fromEntries(table.map((entry) => [entry.id, entry]));
  /** Per small animal: how long it has been following somebody, and eating. */
  const small = new Map();
  /** What has ever been seen, and when. Survives the day and the save. */
  let log = {};
  let sinceRoll = 0;
  let rolls = 0;

  /** A point somewhere inside a room, away from its walls. */
  function spotIn(room, rng) {
    const margin = ft(4);
    for (let tries = 0; tries < 12; tries += 1) {
      const x = room.bounds.minX + margin + rng.next() * Math.max(0, room.bounds.maxX - room.bounds.minX - margin * 2);
      const z = room.bounds.minZ + margin + rng.next() * Math.max(0, room.bounds.maxZ - room.bounds.minZ - margin * 2);
      if (!penetrates(x, z, ft(1.5), 0.05, BODY, world)) return { x, y: 0, z };
    }
    return null;
  }

  /** Which room a point is in, by the rooms' own bounds. */
  function roomAt(x, z) {
    return rooms().find(
      (room) => x >= room.bounds.minX && x <= room.bounds.maxX && z >= room.bounds.minZ && z <= room.bounds.maxZ
    ) ?? null;
  }

  /** Whether a point is somewhere this animal lives, or may be taken. */
  function home(definition, x, z) {
    const room = roomAt(x, z);
    return Boolean(room && (definition.rooms.includes(room.id) || definition.ranges?.includes(room.id)));
  }

  /**
   * Moves a body toward (or away from) a point, off the building and inside
   * its own ground.
   *
   * The leash is the row's `rooms`, which is where the spawner already sends
   * it. Tried whole, then one axis at a time, so a herd following you along a
   * fence slides along it rather than stopping dead at the corner.
   */
  function travel(body, definition, radius, dx, dz, step) {
    const length = Math.hypot(dx, dz);
    if (length < 1e-6 || step <= 0) return false;
    const ux = (dx / length) * step;
    const uz = (dz / length) * step;
    for (const [mx, mz] of [[ux, uz], [ux, 0], [0, uz]]) {
      if (!mx && !mz) continue;
      const next = resolveHorizontal(
        { x: body.position.x + mx, z: body.position.z + mz },
        radius,
        0.05,
        BODY,
        world
      );
      if (!home(definition, next.x, next.z)) continue;
      body.position.x = next.x;
      body.position.z = next.z;
      return true;
    }
    return false;
  }

  /** Who is within `reach` of a point, nearest first. Perception's, when there is one. */
  function whoIsNear(at, reach, player) {
    if (sense) return sense(at, reach);
    if (!player) return [];
    const distance = Math.hypot(player.position.x - at.x, player.position.z - at.z);
    return distance <= reach ? [{ id: 'player', npc: null, position: player.position, distance }] : [];
  }

  /**
   * The food this animal wants most right now, if any.
   *
   * On the ground, or in somebody's hands, within its reach — and the nearest
   * one wins, because an animal does not rank food, it smells it. `held` is
   * who is carrying what; nothing here knows that only the player ever does.
   */
  function lureFor(body, appetite, held) {
    const reach = ft(appetite.reach);
    let best = null;
    const ground = entities.nearest(body.position, { within: reach, tag: 'food' });
    if (ground) best = { position: ground.entity.position, entity: ground.entity, distance: ground.distance };
    for (const holder of held) {
      if (!holder.kind || !hasTag(holder.kind, 'food')) continue;
      const distance = Math.hypot(holder.position.x - body.position.x, holder.position.z - body.position.z);
      if (distance > reach) continue;
      if (!best || distance < best.distance) best = { position: holder.position, holder, distance };
    }
    return best;
  }

  /**
   * Going after food. Returns the holder it got to, if it got all the way.
   *
   * Food on the ground is walked to and eaten. Food in somebody's hands is
   * followed at a gap that starts at `follow` and closes at `closes` a second
   * for as long as it keeps following — so leading an animal is something you
   * can do for a while, and not for ever. At zero the gap is gone, and what
   * arrives is the animal.
   */
  function feed(body, appetite, definition, radius, lure, dt, state) {
    const dx = lure.position.x - body.position.x;
    const dz = lure.position.z - body.position.z;
    const gap = Math.hypot(dx, dz);
    const trot = appetite.trot * dt;

    if (lure.entity) {
      state.following = 0;
      if (gap > MOUTH + radius) {
        travel(body, definition, radius, dx, dz, Math.min(trot, gap - MOUTH));
        state.eating = 0;
      } else {
        state.eating += dt;
        if (state.eating >= EAT) {
          state.eating = 0;
          entities.remove(lure.entity.id);
        }
      }
      return { facing: Math.atan2(dx, dz), arrived: null };
    }

    state.eating = 0;
    state.following += dt;
    const want = Math.max(ft(appetite.nearest), ft(appetite.follow) - ft(appetite.closes) * state.following);
    if (gap > want) travel(body, definition, radius, dx, dz, Math.min(trot, gap - want));
    const arrived = want <= 0 && gap < CONTACT ? lure.holder : null;
    return { facing: Math.atan2(dx, dz), arrived };
  }

  function note(id, day) {
    const entry = log[id] ?? { first: day, last: day, count: 0 };
    entry.last = day;
    entry.count += 1;
    log[id] = entry;
    return entry;
  }

  /** One roll. Returns what it produced, or null. */
  function roll({ hour, day }) {
    const rng = createRng(hashSeed(seed, 'roll', day * 10000 + (rolls += 1)));

    // A row with no chance is never rolled; something has to put it out.
    const open = table.filter((entry) => withinHours(entry.hours, hour) && entry.rooms.length && entry.chance > 0);
    if (!open.length) return null;

    const total = open.reduce((sum, entry) => sum + entry.chance, 0);
    // A roll that lands in the gap is a roll where nothing turned up, which is
    // most of them. Without the gap the desert is a conveyor belt.
    let pick = rng.next() * Math.max(total * 6, total);
    const chosen = open.find((entry) => (pick -= entry.chance) <= 0);
    if (!chosen) return null;

    const eligible = rooms().filter((room) => chosen.rooms.includes(room.id));
    if (!eligible.length) return null;
    const room = rng.pick(eligible);
    const at = spotIn(room, rng);
    if (!at) return null;

    if (chosen.catch) {
      if (entities.all.filter((e) => e.data?.wild).length >= SMALL_LIMIT) return null;
      const entity = entities.spawn({ kind: chosen.kind, position: at, data: { wild: chosen.id } });
      note(chosen.id, day);
      bus?.emit(EVENT.ZONE, { actor: 'wildlife', kind: chosen.id, position: { ...at }, room: room.id });
      return { small: entity, what: chosen.id, room: room.id };
    }

    if (new Set(creatures.map((creature) => creature.group)).size >= BIG_LIMIT) return null;
    const group = `${chosen.id}-${day}-${rolls}`;
    const [fewest, most] = chosen.herd ?? [1, 1];
    const count = fewest + Math.floor(rng.next() * (most - fewest + 1));
    const herd = [];
    for (let i = 0; i < count; i += 1) {
      // The first where the roll landed, the rest around it, anywhere a body fits.
      let spot = i === 0 ? { ...at } : null;
      for (let tries = 0; !spot && tries < 8; tries += 1) {
        const angle = rng.next() * Math.PI * 2;
        const x = at.x + Math.cos(angle) * ft(3 + rng.next() * 4);
        const z = at.z + Math.sin(angle) * ft(3 + rng.next() * 4);
        // Where the desert sends it, not where it could be taken: `rooms`, and
        // not `ranges`, for every one of the herd as for the first.
        const room = roomAt(x, z);
        const sent = Boolean(room && chosen.rooms.includes(room.id));
        if (sent && !penetrates(x, z, ft(1.5), 0.05, BODY, world)) spot = { x, y: 0, z };
      }
      if (!spot) continue;
      herd.push({
        id: count > 1 ? `${group}-${i}` : group,
        group,
        what: chosen.id,
        definition: chosen,
        position: spot,
        yaw: rng.next() * Math.PI * 2,
        state: 'about',
        charging: 0,
        target: null,
        rest: 0,
        following: 0,
        eating: 0,
      });
    }
    creatures = [...creatures, ...herd];
    note(chosen.id, day);
    bus?.emit(EVENT.ZONE, { actor: 'wildlife', kind: chosen.id, position: { ...at }, room: room.id });
    return { big: herd[0], herd, what: chosen.id, room: room.id };
  }

  /**
   * Moves one of the big ones. Returns whoever it got to this frame, or null.
   *
   * In order: somebody close enough to react to, then food, then nothing. The
   * person holding the food it wants is not somebody to react to — a javelina
   * following you for an orange is not also defending its ground from you —
   * but everybody else still is, so a herd trailing you past a colleague goes
   * for the colleague.
   */
  function step(creature, dt, { player, held }) {
    const definition = creature.definition;
    const appetite = definition.appetite ?? null;
    const radius = ft(definition.radius) * 0.5;
    const lure = appetite ? lureFor(creature, appetite, held) : null;
    const people = whoIsNear(creature.position, ft(definition.notice), player);
    const other = people.find((body) => body.id !== lure?.holder?.id) ?? null;
    if (creature.rest > 0) creature.rest = Math.max(0, creature.rest - dt);

    let arrived = null;
    creature.lured = null;

    if (creature.state === 'charging') {
      // Committed. It goes at who it went for until it gets there or gives up.
      creature.charging -= dt;
      const target = whoIsNear(creature.position, Infinity, player).find((body) => body.id === creature.target);
      if (!target || creature.charging <= 0) {
        creature.state = 'about';
        creature.target = null;
      } else {
        const dx = target.position.x - creature.position.x;
        const dz = target.position.z - creature.position.z;
        travel(creature, definition, radius, dx, dz, definition.charge * dt);
        creature.yaw = Math.atan2(dx, dz);
        if (Math.hypot(dx, dz) < CONTACT) arrived = target;
      }
    } else if (other && definition.charge && creature.rest <= 0) {
      creature.state = 'charging';
      creature.charging = CHARGE_FOR;
      creature.target = other.id;
      creature.following = 0;
      bus?.emit(EVENT.NOISE, {
        actor: 'wildlife',
        cause: 'charge',
        kind: creature.what,
        position: { ...creature.position },
        radius: ft(45),
      });
    } else if (creature.heard && definition.hears && creature.rest <= 0 && !(lure && appetite)) {
      /*
       * Going to see about a noise. Where it was, not who made it: an animal
       * does not know a golf cart from a popper, only that something went off
       * over there. Having got there, it has seen about it.
       */
      creature.state = 'prowling';
      const dx = creature.heard.x - creature.position.x;
      const dz = creature.heard.z - creature.position.z;
      if (Math.hypot(dx, dz) < ft(3)) {
        creature.heard = null;
      } else {
        travel(creature, definition, radius, dx, dz, definition.hears.prowl * dt);
        creature.yaw = Math.atan2(dx, dz);
      }
    } else if (other && definition.flee) {
      creature.state = 'leaving';
      creature.following = 0;
      const dx = creature.position.x - other.position.x;
      const dz = creature.position.z - other.position.z;
      travel(creature, definition, radius, dx, dz, definition.flee * dt);
      creature.yaw = Math.atan2(dx, dz);
    } else if (lure && creature.rest <= 0) {
      creature.state = lure.entity ? 'feeding' : 'following';
      // Whose food, so anybody watching can see who it is following.
      creature.lured = lure.holder?.id ?? null;
      const fed = feed(creature, appetite, definition, radius, lure, dt, creature);
      creature.yaw = fed.facing;
      arrived = fed.arrived;
    } else {
      creature.state = 'about';
      creature.following = 0;
      creature.eating = 0;
    }

    creature.position.y = supportHeight(creature.position.x, creature.position.z, radius, creature.position.y, world);

    // Contact. It goes through the impact bus like anything else that arrives
    // somewhere at speed, and what that costs is decided by whoever is
    // listening — not here.
    if (!arrived) return null;
    creature.state = 'about';
    creature.charging = 0;
    creature.target = null;
    creature.following = 0;
    creature.rest = REST;
    bus?.emit(EVENT.IMPACT, {
      actor: 'wildlife',
      kind: creature.what,
      target: { type: 'body', id: arrived.id },
      position: { ...creature.position },
      energy: 30,
      speed: definition.charge || (appetite?.trot ?? 0),
      broke: false,
    });
    return arrived;
  }

  /**
   * Keeps a herd a herd rather than one animal drawn several times.
   *
   * Except that one with its head down eating holds its ground and the rest
   * are shouldered round it. Without that, five of them at one orange stood in
   * a ring nine tenths of a metre apart, which is wider than a javelina's reach
   * to the orange in the middle: whichever got in was elbowed back out before
   * it had finished, its meal started again, and the herd stood round the one
   * orange for the rest of the night. Nothing had led five to one orange before.
   */
  function elbow() {
    for (let i = 0; i < creatures.length; i += 1) {
      for (let j = i + 1; j < creatures.length; j += 1) {
        const a = creatures[i];
        const b = creatures[j];
        const dx = b.position.x - a.position.x;
        const dz = b.position.z - a.position.z;
        const gap = Math.hypot(dx, dz);
        if (gap >= ELBOW || gap < 1e-6) continue;
        const aHolds = a.eating > 0;
        const bHolds = b.eating > 0;
        if (aHolds && bHolds) continue;
        const push = aHolds || bHolds ? ELBOW - gap : (ELBOW - gap) / 2;
        const bx = b.position.x + (dx / gap) * push;
        const bz = b.position.z + (dz / gap) * push;
        const ax = a.position.x - (dx / gap) * push;
        const az = a.position.z - (dz / gap) * push;
        if (!bHolds && home(b.definition, bx, bz) && !penetrates(bx, bz, ft(b.definition.radius) * 0.5, 0.05, BODY, world)) {
          b.position.x = bx;
          b.position.z = bz;
        }
        if (!aHolds && home(a.definition, ax, az) && !penetrates(ax, az, ft(a.definition.radius) * 0.5, 0.05, BODY, world)) {
          a.position.x = ax;
          a.position.z = az;
        }
      }
    }
  }

  /**
   * The small ones that eat. Nothing else about them moves: a scorpion sits
   * where it was put until somebody picks it up, and so does a quail that is
   * not hungry. One that is goes to the food, or trails whoever has it.
   */
  function forage(dt, held) {
    const seen = new Set();
    for (const entity of entities.all) {
      if (entity.state !== 'world' || !entity.data?.wild) continue;
      const definition = rows[entity.data.wild];
      const appetite = definition?.appetite;
      if (!appetite) continue;
      seen.add(entity.id);
      const state = small.get(entity.id) ?? { following: 0, eating: 0 };
      small.set(entity.id, state);
      const lure = lureFor(entity, appetite, held);
      if (!lure) {
        state.following = 0;
        state.eating = 0;
        continue;
      }
      const radius = ft(OBJECTS[entity.kind]?.radius ?? 0.2);
      const fed = feed(entity, appetite, definition, radius, lure, dt, state);
      entity.yaw = fed.facing;
    }
    for (const id of small.keys()) if (!seen.has(id)) small.delete(id);
  }

  /*
   * What the ones that listen hear. A noise that is not an animal's, within the
   * row's reach and within the noise's own radius, is somewhere to go and see;
   * the newest one wins, the way a person looks round at the last bang.
   */
  bus?.on(EVENT.NOISE, (event) => {
    if (event.actor === 'wildlife' || !event.position) return;
    for (const creature of creatures) {
      const hears = creature.definition.hears;
      if (!hears) continue;
      const gap = Math.hypot(event.position.x - creature.position.x, event.position.z - creature.position.z);
      if (gap > Math.min(ft(hears.reach), (event.radius ?? Infinity) * (hears.acuity ?? 1))) continue;
      creature.heard = { x: event.position.x, z: event.position.z };
    }
  });

  return {
    get creatures() {
      return creatures;
    },

    /**
     * Puts one out, where something needs it: the whole group its row comes
     * in, around the point, on its own ground. For a mission, and only ever
     * one that says so; the desert does not use it.
     */
    spawn(id, at, { day = 1, tag = null } = {}) {
      const definition = rows[id];
      if (!definition || definition.catch) return null;
      const group = tag ?? `${id}-forced-${day}-${(rolls += 1)}`;
      const [fewest] = definition.herd ?? [1, 1];
      const out = [];
      for (let i = 0; i < fewest; i += 1) {
        out.push({
          id: fewest > 1 ? `${group}-${i}` : group,
          group,
          what: id,
          definition,
          position: { x: at.x + i * ft(2), y: 0, z: at.z },
          yaw: 0,
          state: 'about',
          charging: 0,
          target: null,
          rest: 0,
          following: 0,
          eating: 0,
          heard: null,
        });
      }
      creatures = [...creatures, ...out];
      note(id, day);
      return out;
    },

    /** Takes a group away again: gone back over the fence. */
    dismiss(group) {
      creatures = creatures.filter((creature) => creature.group !== group);
    },
    /** Everything ever seen, by species. Persists across days and saves. */
    get sightings() {
      return log;
    },

    /**
     * One frame. `held` is who is carrying what, as `{ id, position, kind }`;
     * the player is on it when they have something in their hands.
     */
    update(dt, { clock, player, held = [] }) {
      sinceRoll += dt * (clock?.scale ?? 1);
      const minutes = clock ? clock.minutes : 0;
      const hour = Math.floor(minutes / 60) % 24;
      const day = clock?.day ?? 1;

      // Rolls are on the shift clock, because what is about depends on the
      // hour and the hour is the shift's.
      const per = (EVERY * 60) / Math.max(1, clock?.scale ?? 1);
      if (sinceRoll > per) {
        sinceRoll = 0;
        roll({ hour, day });
      }

      const hits = [];
      for (const creature of creatures) {
        const arrived = step(creature, dt, { player, held });
        // The player is the only body anybody downstream needs told about by
        // return. Everybody else hears it on the bus with everything else.
        if (arrived?.id === 'player') hits.push(creature);
      }
      elbow();
      forage(dt, held);
      // The big ones do not hang around. One pass through and they are gone,
      // which is what makes them weather rather than content.
      creatures = creatures.filter((creature) => creature.state !== 'leaving' || Math.hypot(
        creature.position.x - (player?.position.x ?? 0),
        creature.position.z - (player?.position.z ?? 0)
      ) < ft(90));
      return hits;
    },

    /** Forces a roll. For a test, and for a script that cannot wait for dusk. */
    roll,

    toJSON: () => ({ log: { ...log } }),
    load(state) {
      log = state?.log ? { ...state.log } : {};
    },
    clear() {
      creatures = [];
      small.clear();
      sinceRoll = 0;
    },
    /** The log is the one thing a new run does not wipe. */
    forgetEverything() {
      log = {};
    },
  };
}
